import React from 'react';
import { setReturn } from '../operations/ReturnToList.jsx';
const { useState, useEffect } = React;
import {
  COACHING_MODALITIES, COACHING_FOCUS, COACHING_SPECIALTIES, PROCESS_STATUSES, SESSION_STATUSES, ASSIGNMENT_KINDS, ENTRY_KINDS,
  TRANSCRIPT_DEFAULTS, MIN_GROUP_FOR_AGGREGATES, questionnaireFor, label,
} from '../../coachingConfig.js';
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, badgeSt, warnSt, fmtDate, Field, SubTabs } from '../diagnostic/shared.jsx';
import { coachCall, coachingPeopleApi, Visibility, MoreOptions, Row, Empty, goalStatusLabel, commitmentStatusLabel, copy, useCoachingTools } from './shared.jsx';
import PersonPicker from '../people/PersonPicker.jsx';
import ProgramEditor from './ProgramEditor.jsx';
import TeamProgress from './TeamProgress.jsx';
import SponsorAccess from './SponsorAccess.jsx';
import SessionPanel from './SessionPanel.jsx';
import ProcessSummary from './ProcessSummary.jsx';
import OriginNote from '../serviceStart/OriginNote.jsx';

// One process: the agreement (focus and goals), the people (invitations and
// questionnaires), the adapted program, sessions, activities, follow-up and
// messages. Every block says who can see it.

function GoalsEditor({ goals, onChange }) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {goals.map((g, i) => (
        <Row key={g.id || i}>
          <input style={{ ...inputSt, flex: 1, width: 'auto' }} value={g.text} maxLength={400} onChange={e => onChange(goals.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
          <button className="action-btn" onClick={() => onChange(goals.filter((_, j) => j !== i))}>✕</button>
        </Row>
      ))}
      <button className="action-btn" style={{ justifySelf: 'start' }} disabled={goals.length >= 8} onClick={() => onChange([...goals, { text: '' }])}>+ Objetivo</button>
    </div>
  );
}

function AgreementTab({ view, act, people = [] }) {
  const p = view.process;
  const team = p.modality === 'team';
  const individualMember = !team ? view.members.find(m => m.status !== 'left') : null;
  const [form, setForm] = useState(() => ({
    focus: p.agreement?.focus || p.focus || 'personal', specialties: p.agreement?.specialties || p.specialties || [],
    sharedGoals: p.agreement?.sharedGoals || [], individualGoals: individualMember?.agreement?.goals || [], note: '', confirmChange: false,
  }));
  const [settings, setSettings] = useState({ name: p.name, groupChannel: p.groupChannel, sponsorName: p.sponsorName || '', sponsorEmail: p.sponsorEmail || '', transcriptRetentionDays: p.transcriptRetentionDays ?? TRANSCRIPT_DEFAULTS.retentionDays, startsOn: p.startsOn || '', endsOn: p.endsOn || '' });
  const changing = p.focusAgreed && (form.focus !== p.agreement?.focus || JSON.stringify([...form.specialties].sort()) !== JSON.stringify([...(p.agreement?.specialties || [])].sort()));
  const toggleSpec = (k) => setForm(f => ({ ...f, specialties: f.specialties.includes(k) ? f.specialties.filter(x => x !== k) : [...f.specialties, k] }));
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={cardSt}>
        <Row style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Acuerdo inicial</h3>
          <Visibility kind={team ? 'group' : 'shared'} label={team ? 'Foco y objetivos compartidos: equipo y quien contrata' : 'Tú y la persona'} />
        </Row>
        <p style={mutedSt}>
          {p.focusAgreed ? `Acordado el ${fmtDate(p.agreement.agreedAt, true)} (versión ${p.agreement.version}). Cambiar el foco o las especialidades necesita un nuevo acuerdo explícito.` : 'Se registra en la primera sesión, después de revisar el cuestionario. El foco no cambia por una sugerencia de la IA.'}
        </p>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Foco acordado">
            <select style={{ ...inputSt, maxWidth: 320 }} value={form.focus} onChange={e => setForm({ ...form, focus: e.target.value })}>
              {COACHING_FOCUS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </Field>
          <div>
            <span style={labelSt}>Especialidades pedidas</span>
            <Row>{COACHING_SPECIALTIES.map(s => <label key={s.key} style={{ fontSize: 13 }}><input type="checkbox" checked={form.specialties.includes(s.key)} onChange={() => toggleSpec(s.key)} /> {s.label}</label>)}</Row>
          </div>
          {team ? (
            <div><span style={labelSt}>Objetivos compartidos del equipo</span><GoalsEditor goals={form.sharedGoals} onChange={g => setForm({ ...form, sharedGoals: g })} /></div>
          ) : (
            <div><span style={labelSt}>Objetivos de la persona</span><GoalsEditor goals={form.individualGoals} onChange={g => setForm({ ...form, individualGoals: g })} /></div>
          )}
          {team && <div style={mutedSt}>Los objetivos privados de cada integrante se registran en la pestaña Personas y los ven solo esa persona y tú.</div>}
          <Field label={changing ? 'Nota del nuevo acuerdo (obligatoria)' : 'Nota (opcional)'}><textarea style={textareaSt} value={form.note} maxLength={1000} onChange={e => setForm({ ...form, note: e.target.value })} /></Field>
          {changing && (
            <label style={{ ...warnSt, marginBottom: 0, fontSize: 13, display: 'flex', gap: 8 }}>
              <input type="checkbox" checked={form.confirmChange} onChange={e => setForm({ ...form, confirmChange: e.target.checked })} />
              Confirmo que el cambio de foco o especialidades se acordó explícitamente con {team ? 'el equipo' : 'la persona'}.
            </label>
          )}
          <Row><button className="action-btn btn-primary" onClick={() => act({ action: 'recordAgreement', ...form, sharedGoals: team ? form.sharedGoals : undefined, individualGoals: team ? undefined : form.individualGoals })}>Registrar acuerdo</button></Row>
        </div>
        {p.agreement?.history?.length > 0 && (
          <MoreOptions title={`Acuerdos anteriores (${p.agreement.history.length})`}>
            {p.agreement.history.map((h, i) => <div key={i} style={{ fontSize: 12 }}>v{h.version} · {fmtDate(h.agreedAt, true)} · foco {label(COACHING_FOCUS, h.focus).toLowerCase()}{h.note ? ` · ${h.note}` : ''}</div>)}
          </MoreOptions>
        )}
      </div>

      <div style={cardSt}>
        <h3 style={{ marginTop: 0 }}>Datos del proceso</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <Field label="Nombre"><input style={inputSt} value={settings.name} onChange={e => setSettings({ ...settings, name: e.target.value })} /></Field>
          <Field label="Inicio"><input type="date" style={inputSt} value={settings.startsOn} onChange={e => setSettings({ ...settings, startsOn: e.target.value })} /></Field>
          <Field label="Fin"><input type="date" style={inputSt} value={settings.endsOn} onChange={e => setSettings({ ...settings, endsOn: e.target.value })} /></Field>
        </div>
        <MoreOptions title="Equipo, quien contrata y transcripciones">
          {team && <>
            <label style={{ fontSize: 13 }}><input type="checkbox" checked={settings.groupChannel} onChange={e => setSettings({ ...settings, groupChannel: e.target.checked })} /> Canal de mensajes del equipo</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              <Field label="Quien contrata o lidera"><input style={inputSt} value={settings.sponsorName} onChange={e => setSettings({ ...settings, sponsorName: e.target.value })} /></Field>
              <Field label="Email"><input style={inputSt} value={settings.sponsorEmail} onChange={e => setSettings({ ...settings, sponsorEmail: e.target.value })} /></Field>
            </div>
          </>}
          <Field label="Conservar transcripciones (días)" hint="Aplica a las que se carguen desde ahora. 0 = se borran al cerrar el proceso.">
            <input type="number" min={0} max={TRANSCRIPT_DEFAULTS.maxRetentionDays} style={{ ...inputSt, width: 120 }} value={settings.transcriptRetentionDays} onChange={e => setSettings({ ...settings, transcriptRetentionDays: Number(e.target.value) })} />
          </Field>
        </MoreOptions>
        <Row style={{ marginTop: 10 }}><button className="action-btn" onClick={() => act({ action: 'updateProcess', process: settings })}>Guardar datos</button></Row>
      </div>

      {team && <SponsorAccess view={view} act={act} people={people} />}

      {p.status !== 'closed' && (
        <div style={cardSt}>
          <h3 style={{ marginTop: 0 }}>Cerrar el proceso</h3>
          <p style={mutedSt}>Las personas siguen viendo lo compartido, pero ya no reservan turnos. Con conservación 0, las transcripciones se borran ahora; si no, en su fecha.</p>
          <button className="action-btn" onClick={() => window.confirm('¿Cerrar este proceso?') && act({ action: 'closeProcess', confirm: true })}>Cerrar proceso</button>
        </div>
      )}
    </div>
  );
}

function PeopleTab({ view, act, people }) {
  const p = view.process;
  const [adding, setAdding] = useState('');
  const questions = questionnaireFor({ focus: p.focus || 'personal', modality: p.modality, specialties: p.specialties });
  const inProcess = new Set(view.members.filter(m => m.status !== 'left').map(m => m.personId));
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {view.members.filter(m => m.status !== 'left').map(m => (
        <MemberCard key={m.id} m={m} p={p} questions={questions} act={act} />
      ))}
      {p.modality === 'team' && (
        <div style={cardSt}>
          <span style={labelSt}>Agregar integrante</span>
          <div style={{ maxWidth: 560 }}>
            <PersonPicker key={adding} api={coachingPeopleApi(people)} exclude={[...inProcess]} onPick={(card) => { setAdding(card.id); act({ action: 'addMember', personId: card.id }); }} />
          </div>
          <div style={{ ...mutedSt, marginTop: 4 }}>Busca una persona o créala aquí. La empresa es la del proceso, no la de la persona.</div>
        </div>
      )}
    </div>
  );
}

/** Opens a person's card from this process, with the way back to it (the same screen; nothing is saved by leaving). */
export function openPersonFromProcess(personId, process) {
  setReturn(`«${process.name}»`, { page: 'coaching', processId: process.id }, ['people', 'accounts']);
  window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', personId } }));
}

/**
 * Who this process is about, each by its own explicit link (never inferred): the company linked to the process
 * (Account Id), the person responsible for a team (Sponsor Person Id; a name without a card is only text) and the
 * members. Each opens its own card, with the way back here.
 */
function ProcessRelation({ view, onTab }) {
  const p = view.process;
  const team = p.modality === 'team';
  const active = view.members.filter(m => m.status !== 'left');
  const linkBtn = { fontSize: 11, padding: '2px 8px' };
  const parts = [];
  if (p.accountId) parts.push(<span key="acc">Empresa vinculada al proceso <button type="button" className="action-btn" style={linkBtn} onClick={() => { setReturn(`«${p.name}»`, { page: 'coaching', processId: p.id }, ['accounts']); window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'accounts', accountId: p.accountId } })); }}>Abrir empresa</button></span>);
  if (team && (view.sponsor?.personId || p.sponsorName)) parts.push(<span key="sp">Quien contrata o lidera: {view.sponsor?.name || p.sponsorName || '—'} {view.sponsor?.personId ? <button type="button" className="action-btn" style={linkBtn} onClick={() => openPersonFromProcess(view.sponsor.personId, p)}>Ficha de persona</button> : <span style={mutedSt}>(sin ficha vinculada)</span>}</span>);
  if (team) parts.push(<span key="m">Integrantes: {active.length} <button type="button" className="action-btn" style={linkBtn} onClick={() => onTab('people')}>Ver integrantes</button></span>);
  else if (active[0]?.person?.id) parts.push(<span key="m">Integrante: {active[0].person.name} <button type="button" className="action-btn" style={linkBtn} onClick={() => openPersonFromProcess(active[0].person.id, p)}>Ficha de persona</button></span>);
  if (!parts.length) return null;
  return <div aria-label="Relación con el cliente" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, margin: '-6px 0 12px' }}>{parts}</div>;
}

function MemberCard({ m, p, questions, act }) {
  const [goals, setGoals] = useState(m.agreement?.goals || []);
  const team = p.modality === 'team';
  return (
    <div style={cardSt}>
      <Row style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>{m.person?.name || 'Persona'} {m.person?.id && <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 6 }} onClick={() => openPersonFromProcess(m.person.id, p)}>Ficha de persona</button>}</h3>
        <span style={mutedSt}>{m.firstAccessAt ? `Entró el ${fmtDate(m.firstAccessAt)}` : 'Todavía no entró'} · cuestionario {m.questionnaireStatus === 'submitted' ? 'enviado' : m.questionnaireStatus === 'draft' ? 'en curso' : 'sin empezar'}</span>
      </Row>
      <MemberAccess m={m} act={act} />
      <MoreOptions title="Cuestionario previo" open={m.questionnaireStatus === 'submitted'}>
        <Visibility kind="shared" label="La persona y tú" />
        {m.questionnaireStatus !== 'submitted' ? <Empty>Todavía no lo envió.</Empty> : questions.map(q => (
          <div key={q.key} style={{ fontSize: 13 }}><div style={{ ...mutedSt }}>{q.label}</div><div style={{ whiteSpace: 'pre-wrap' }}>{m.questionnaire?.[q.key] || '—'}</div></div>
        ))}
      </MoreOptions>
      {team && (
        <MoreOptions title="Objetivos privados de esta persona" open={!!m.agreement?.goals?.length}>
          <Visibility kind="shared" label="Solo esta persona y tú" />
          <GoalsEditor goals={goals} onChange={setGoals} />
          <Row><button className="action-btn" onClick={() => act({ action: 'recordIndividualAgreement', memberId: m.id, goals })}>Guardar objetivos</button></Row>
        </MoreOptions>
      )}
      {team && <div style={{ marginTop: 10 }}><button className="action-btn" style={{ fontSize: 11 }} onClick={() => window.confirm('¿Quitar a esta persona del equipo? Deja de ver este proceso; su espacio y sus otros servicios siguen igual.') && act({ action: 'removeMember', memberId: m.id })}>Quitar del equipo</button></div>}
    </div>
  );
}

/**
 * The person enters with their one personal access, pointed to this process. Copying never changes it;
 * renewing or revoking is done on the person's card. An earlier process link can only be turned off.
 */
function MemberAccess({ m, act }) {
  const [note, setNote] = useState('');
  const a = m.access;
  const copyLink = async () => {
    setNote('');
    try {
      let r = await coachCall({ action: 'personAccess', personId: m.person.id, target: `coaching:${m.id}` });
      // Copying only reads; a person without any link gets one only if you say so
      if (r.never && window.confirm('Todavía no tiene link personal. ¿Crearlo ahora?')) r = await coachCall({ action: 'personAccess', op: 'create', personId: m.person.id, target: `coaching:${m.id}` });
      if (!r.targetLink) { setNote(r.review ? 'Su acceso está para revisar (ficha anterior): créalo o déjalo sin acceso desde su ficha en Personas.' : r.revoked ? 'El acceso de esta persona está revocado. Renuévalo desde su ficha en Personas.' : 'Todavía no tiene link personal.'); return; }
      setNote((await copy(r.targetLink)) ? 'Copiado: es su link personal, abre este proceso. Envíaselo solo a esta persona.' : r.targetLink);
    } catch (e) { setNote(e.message); }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <span style={labelSt}>Acceso de la persona</span>
      <Row>
        <button className="action-btn btn-primary" disabled={!m.person?.id} onClick={copyLink}>Copiar link de la persona</button>
        <span style={mutedSt}>{a?.active ? `Acceso activo${a.lastAccessAt ? ` · último ingreso ${fmtDate(a.lastAccessAt)}` : ''}` : a?.revoked ? 'Acceso revocado (se renueva en su ficha)' : a?.review ? 'Acceso para revisar en su ficha' : 'Todavía sin link personal'}</span>
      </Row>
      {note && <div style={{ ...mutedSt, marginTop: 4 }}>{note}</div>}
      {m.legacyInvite && (
        <Row style={{ marginTop: 6 }}>
          <span style={mutedSt}>Tiene un link anterior de este proceso (abre solo este proceso).</span>
          <button className="action-btn" style={{ fontSize: 11 }} onClick={() => window.confirm('¿Desactivar el link anterior de este proceso? Su link personal sigue funcionando.') && act({ action: 'revokeLegacyInvite', memberId: m.id })}>Desactivar link anterior</button>
        </Row>
      )}
    </div>
  );
}

function ActivitiesTab({ view, act }) {
  const p = view.process;
  const team = p.modality === 'team';
  const tools = useCoachingTools();
  const members = view.members.filter(m => m.status !== 'left');
  const empty = { title: '', kind: 'exercise', instructions: '', link: '', dueOn: '', memberId: team ? '' : members[0]?.id, methodEntryId: '' };
  const [form, setForm] = useState(null);
  const nameOf = (id) => members.find(m => m.id === id)?.person?.name || 'Persona';
  return (
    <div style={cardSt}>
      <Row style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Actividades y materiales</h3>
        <button className="action-btn btn-primary" onClick={() => setForm(empty)}>+ Actividad</button>
      </Row>
      {form && (
        <div style={{ ...cardSt, background: 'rgba(30,42,44,0.02)' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            <Field label="Título"><input style={inputSt} value={form.title} maxLength={160} onChange={e => setForm({ ...form, title: e.target.value })} /></Field>
            <Row>
              <Field label="Tipo"><select style={inputSt} value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>{ASSIGNMENT_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}</select></Field>
              {team && <Field label="Para"><select style={inputSt} value={form.memberId || ''} onChange={e => setForm({ ...form, memberId: e.target.value })}><option value="">Todo el equipo</option>{members.map(m => <option key={m.id} value={m.id}>{m.person?.name}</option>)}</select></Field>}
            </Row>
            <Field label="Instrucciones"><textarea style={textareaSt} value={form.instructions} maxLength={3000} onChange={e => setForm({ ...form, instructions: e.target.value })} /></Field>
            <MoreOptions title="Enlace, fecha y herramienta de la biblioteca">
              <Field label="Enlace (opcional)"><input style={inputSt} value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} /></Field>
              <Field label="Para cuándo"><input type="date" style={{ ...inputSt, width: 180 }} value={form.dueOn || ''} onChange={e => setForm({ ...form, dueOn: e.target.value })} /></Field>
              <Field label="Herramienta de la biblioteca"><select style={inputSt} value={form.methodEntryId || ''} onChange={e => { const t = tools.find(x => x.id === e.target.value); setForm({ ...form, methodEntryId: e.target.value, methodEntryVersion: t?.version }); }}><option value="">—</option>{tools.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}</select></Field>
            </MoreOptions>
            <Row><button className="action-btn btn-primary" onClick={() => act({ action: 'saveAssignment', id: form.id, assignment: form }).then(ok => ok && setForm(null))}>Guardar</button><button className="action-btn" onClick={() => setForm(null)}>Cancelar</button></Row>
          </div>
        </div>
      )}
      {!view.assignments.length ? <Empty>Todavía no hay actividades.</Empty> : view.assignments.filter(a => a.status !== 'withdrawn').map(a => (
        <div key={a.id} style={{ borderBottom: '1px solid var(--globant-border)', padding: '8px 0', fontSize: 13 }}>
          <Row>
            <b>{a.title}</b> <span style={badgeSt(a.status === 'done')}>{a.status === 'done' ? 'Hecha' : 'Asignada'}</span>
            {a.memberId ? <Visibility kind="shared" label={`${nameOf(a.memberId)} y tú`} /> : <Visibility kind="group" />}
            {a.origin === 'ai_plan' && <span style={mutedSt}>desde una propuesta aprobada</span>}
          </Row>
          {a.instructions && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{a.instructions}</div>}
          <Row style={{ marginTop: 4 }}>
            {a.dueOn && <span style={mutedSt}>Para el {fmtDate(a.dueOn)}</span>}
            <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setForm({ ...a })}>Editar</button>
            <button className="action-btn" style={{ fontSize: 11 }} onClick={() => act({ action: 'withdrawAssignment', id: a.id })}>Retirar</button>
          </Row>
        </div>
      ))}
    </div>
  );
}

/** Follow-up: goals, commitments and what the person shared. Never a score. */
function ProgressTab({ view, act }) {
  const p = view.process;
  const team = p.modality === 'team';
  const members = view.members.filter(m => m.status !== 'left');
  const commitmentsOf = (memberId) => view.sessions.filter(s => (memberId ? s.memberId === memberId : !s.memberId)).flatMap(s => (s.commitments || []).map(c => ({ ...c, session: s.title })));
  const GoalList = ({ goals, memberId }) => (!goals?.length ? <Empty>Sin objetivos registrados.</Empty> : goals.map(g => (
    <Row key={g.id} style={{ fontSize: 13 }}>
      <span style={{ flex: 1 }}>{g.text}</span>
      <select style={{ ...inputSt, width: 'auto' }} value={g.status || 'open'} onChange={e => act({ action: 'setGoalStatus', memberId, goalId: g.id, status: e.target.value })}>
        {Object.entries(goalStatusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </Row>
  )));
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={mutedSt}>El seguimiento combina objetivos, acciones y reflexión. No hay puntajes de las personas.</p>
      {team && (
        <div style={cardSt}>
          <Row style={{ justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>Equipo</h3><Visibility kind="group" /></Row>
          <span style={labelSt}>Objetivos compartidos</span>
          <GoalList goals={p.agreement?.sharedGoals} />
          <span style={{ ...labelSt, marginTop: 10 }}>Compromisos del equipo</span>
          {commitmentsOf(null).length ? commitmentsOf(null).map(c => <div key={c.id} style={{ fontSize: 13 }}>{c.text} · {commitmentStatusLabel[c.status]} <span style={mutedSt}>({c.session})</span></div>) : <Empty>Sin compromisos.</Empty>}
        </div>
      )}
      {members.map(m => {
        const shared = view.sharedEntries.filter(e => e.memberId === m.id);
        return (
          <div key={m.id} style={cardSt}>
            <Row style={{ justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>{m.person?.name}</h3><Visibility kind="shared" label="Esta persona y tú" /></Row>
            <span style={labelSt}>Objetivos</span>
            <GoalList goals={m.agreement?.goals} memberId={m.id} />
            <span style={{ ...labelSt, marginTop: 10 }}>Compromisos y próximos pasos</span>
            {commitmentsOf(m.id).length ? commitmentsOf(m.id).map(c => <div key={c.id} style={{ fontSize: 13 }}>{c.text} · {commitmentStatusLabel[c.status]} <span style={mutedSt}>({c.session})</span></div>) : <Empty>Sin compromisos.</Empty>}
            <span style={{ ...labelSt, marginTop: 10 }}>Lo que compartió</span>
            {shared.length ? shared.map(e => (
              <div key={e.id} style={{ fontSize: 13, borderLeft: '2px solid var(--globant-border)', paddingLeft: 8, margin: '6px 0' }}>
                <div style={mutedSt}>{label(ENTRY_KINDS, e.kind)} · {fmtDate(e.sharedAt || e.createdAt)}</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{e.text}</div>
              </div>
            )) : <Empty>Nada compartido todavía. Sus reflexiones privadas no se ven acá.</Empty>}
          </div>
        );
      })}
    </div>
  );
}

function MessagesTab({ view, initialThread }) {
  const p = view.process;
  const members = view.members.filter(m => m.status !== 'left');
  const valid = (t) => t === 'group' ? (p.modality === 'team' && p.groupChannel) : members.some(m => m.id === t);
  const [target, setTarget] = useState(valid(initialThread) ? initialThread : members[0]?.id || 'group');
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const body = target === 'group' ? { processId: p.id } : { processId: p.id, memberId: target };
  useEffect(() => { setMessages(null); setError(''); coachCall({ action: 'thread', ...body }).then(d => setMessages(d.messages)).catch(e => setError(e.message)); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  // The text stays in the box until the message is saved: an error never loses it
  const send = async () => { setError(''); setSending(true); try { const d = await coachCall({ action: 'sendMessage', ...body, body: text }); setMessages(d.messages); setText(''); } catch (e) { setError(`No se envió: ${e.message} Tu texto sigue acá; vuelve a intentarlo.`); } setSending(false); };
  const nameOf = (id) => members.find(m => m.id === id)?.person?.name || 'Integrante';
  return (
    <div style={cardSt}>
      <Row style={{ marginBottom: 10 }}>
        <select style={{ ...inputSt, width: 'auto' }} value={target} onChange={e => setTarget(e.target.value)}>
          {members.map(m => <option key={m.id} value={m.id}>Privado · {m.person?.name}</option>)}
          {p.modality === 'team' && p.groupChannel && <option value="group">Canal del equipo</option>}
        </select>
        {target === 'group' ? <Visibility kind="group" /> : <Visibility kind="shared" label="Solo esta persona y tú" />}
      </Row>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <div style={{ display: 'grid', gap: 6, maxHeight: 360, overflow: 'auto', marginBottom: 10 }}>
        {!messages ? 'Cargando…' : !messages.length ? <Empty>Sin mensajes.</Empty> : messages.map(m => (
          <div key={m.id} style={{ justifySelf: m.author === 'coach' ? 'end' : 'start', maxWidth: '80%', background: m.author === 'coach' ? 'rgba(91,191,181,0.12)' : 'rgba(30,42,44,0.05)', borderRadius: 10, padding: '6px 10px', fontSize: 13 }}>
            <div style={{ ...mutedSt, fontSize: 10 }}>{m.author === 'coach' ? 'Tú' : nameOf(m.authorMemberId)} · {fmtDate(m.createdAt, true)}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
          </div>
        ))}
      </div>
      <Row>
        <textarea style={{ ...textareaSt, flex: 1 }} value={text} maxLength={4000} onChange={e => setText(e.target.value)} placeholder="Escribe un mensaje" />
        <button className="action-btn btn-primary" disabled={!text.trim() || sending} onClick={send}>{sending ? 'Enviando…' : 'Enviar'}</button>
      </Row>
    </div>
  );
}

const INITIAL_TAB = { messages: 'messages', sessions: 'sessions', activities: 'activities', followup: 'progress', team: 'team' };

export default function ProcessDetail({ processId, onBack, people, initial }) {
  const [view, setView] = useState(null);
  // Arriving from a conversation or a notification: straight to that tab (and thread)
  const [tab, setTab] = useState(INITIAL_TAB[initial?.tab] || 'summary');
  const [error, setError] = useState('');
  const [program, setProgram] = useState(null);
  // Arriving at one session (from Personas, «Por atender» or a team row): that session's panel, directly
  const [openSession, setOpenSession] = useState(initial?.session || null);
  const tools = useCoachingTools();
  const load = () => coachCall({ action: 'getProcess', processId }).then(v => { setView(v); return v; }).catch(e => setError(e.message));
  useEffect(() => { load(); }, [processId]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Runs an action of this process and refreshes it. Resolves true if it worked. */
  const act = async (body) => {
    setError('');
    try { const v = await coachCall({ processId, ...body }); if (v?.process) setView(v); else await load(); return true; }
    catch (e) { setError(e.message); return false; }
  };
  if (!view) return <div style={cardSt}>{error || 'Cargando…'}</div>;
  const p = view.process;
  const team = p.modality === 'team';
  const memberName = (id) => view.members.find(m => m.id === id)?.person?.name || '';
  return (
    <div>
      <Row style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <button className="action-btn" onClick={onBack}>← Acompañamientos</button>
        <Row>
          <span style={badgeSt(p.status === 'active')}>{label(PROCESS_STATUSES, p.status)}</span>
          <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: 'var(--area-coaching-bg)', color: 'var(--area-coaching)' }}>Solo coach</span>
        </Row>
      </Row>
      <h2 style={{ margin: '0 0 4px', fontSize: 26, letterSpacing: '-0.3px' }}>{p.name}</h2>
      <div style={{ ...mutedSt, fontSize: 13, marginBottom: 14 }}>
        {label(COACHING_MODALITIES, p.modality)} · foco {label(COACHING_FOCUS, p.focus).toLowerCase()} {p.focusAgreed ? '(acordado)' : '(propuesto)'}
        {p.specialties.length ? ` · ${p.specialties.map(s => label(COACHING_SPECIALTIES, s)).join(', ')}` : ''} · programa: {p.snapshot?.name} (copia de la v{p.programVersion})
      </div>
      <ProcessRelation view={view} onTab={(t) => { setTab(t); setOpenSession(null); }} />
      <OriginNote origins={view.serviceOrigin} />
      <SubTabs tabs={[['summary', 'Resumen'], ['agreement', 'Acuerdo'], ['people', team ? 'Integrantes' : 'Integrante'], ...(team ? [['team', 'Progreso']] : []), ['program', 'Programa'], ['sessions', 'Sesiones'], ['activities', 'Actividades'], ['progress', 'Avances'], ['messages', 'Mensajes']]} value={tab} onChange={t => { setTab(t); setOpenSession(null); }} />
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      {tab === 'summary' && <ProcessSummary view={view} memberName={memberName} onGo={(g) => { setTab(g.tab); setOpenSession(g.sessionId || null); }} />}
      {tab === 'agreement' && <AgreementTab view={view} act={act} people={people} key={p.agreement?.version || 0} />}
      {tab === 'people' && <PeopleTab view={view} act={act} people={people} />}
      {tab === 'program' && (
        <div style={cardSt}>
          <p style={mutedSt}>Esta es la copia del programa para este proceso. Los cambios no tocan la plantilla. Las sesiones nuevas de la secuencia se agregan al guardar.</p>
          <ProgramEditor value={program || { ...p.snapshot, modality: p.modality, focus: p.focus }} onChange={setProgram} fixed tools={tools} />
          <Row style={{ marginTop: 12 }}>
            <button className="action-btn btn-primary" disabled={!program} onClick={() => act({ action: 'adaptProgram', program }).then(ok => ok && setProgram(null))}>Guardar adaptación</button>
            {program && <button className="action-btn" onClick={() => setProgram(null)}>Descartar cambios</button>}
          </Row>
        </div>
      )}
      {tab === 'sessions' && (openSession
        ? <SessionPanel view={view} sessionId={openSession} act={act} onBack={() => setOpenSession(null)} reload={load} />
        : (
          <div style={cardSt}>
            {view.sessions.map(s => (
              <button key={s.id} onClick={() => setOpenSession(s.id)} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--globant-border)', padding: '8px 0', color: 'var(--globant-text)', cursor: 'pointer', fontSize: 13 }}>
                <Row>
                  <b>{s.number ? `${s.number}. ` : ''}{s.title}</b>
                  <span style={badgeSt(s.kind === 'group')}>{s.kind === 'group' ? 'grupal' : team ? `individual · ${memberName(s.memberId)}` : 'individual'}</span>
                  <span style={badgeSt(s.status === 'done')}>{label(SESSION_STATUSES, s.status)}</span>
                  {s.startsAt && <span style={mutedSt}>{fmtDate(s.startsAt, true)}</span>}
                  {s.plan && <span style={mutedSt}>· plan aprobado</span>}
                  {s.summaryStatus === 'draft' && <span style={mutedSt}>· resumen sin aprobar</span>}
                </Row>
              </button>
            ))}
            <Row style={{ marginTop: 10 }}>
              {team && <button className="action-btn" onClick={() => act({ action: 'addSession', kind: 'group', title: 'Sesión grupal adicional' })}>+ Sesión grupal</button>}
              {view.members.filter(m => m.status !== 'left').map(m => <button key={m.id} className="action-btn" onClick={() => act({ action: 'addSession', kind: 'individual', memberId: m.id, title: 'Sesión adicional' })}>+ Individual{team ? ` · ${m.person?.name}` : ''}</button>)}
            </Row>
          </div>
        ))}
      {tab === 'activities' && <ActivitiesTab view={view} act={act} />}
      {tab === 'progress' && <ProgressTab view={view} act={act} />}
      {tab === 'messages' && <MessagesTab view={view} initialThread={initial?.thread} />}
      {tab === 'team' && team && <TeamProgress processId={processId} />}
    </div>
  );
}
